// Core imports
import { t } from 'i18next';
import React, { Fragment, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useParams } from 'react-router-dom';

// Component imports
import { Button, Col, Form, Input, Row, Spin, Typography, Switch } from 'antd';

// Misc imports
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';

const CREATE_VARIABLE_MODAL_KEY = 'create-static-variable';

const StaticVariableForm = ({
  onSubmitSuccess = () => {},
  form,
  onCancel = () => {},
  bodyClassName,
  footerClassName,
  setHasPendingChanges,
  isLoading,
  ...restProps
}) => {
  const { orgId, tenantId } = useParams();
  const { data: staticVariables } = getCurrentQueryState('fetchStaticVariables', { orgId, tenantId, urlParams: { page: 0, pageSize: 9999 } });
  const location = useLocation();

  const initialValues = useMemo(() => {
    const urlSearchParams = new URLSearchParams(location.search);
    const staticVariableToEdit = urlSearchParams.get('edit');

    if (staticVariableToEdit !== CREATE_VARIABLE_MODAL_KEY && staticVariables?.items) {
      const variable = staticVariables.items.find(item => item.name === staticVariableToEdit);
      return variable ? { ...variable, isConstant: variable?.isConstant ?? false } : undefined;
    }

    return {
      name: '',
      value: '',
      description: '',
      isConstant: false
    };
  }, [location.search, staticVariables]);

  const handleSubmit = async (values) => {
    try {
      await onSubmitSuccess(values);
    } catch (error) {}
  };

  useEffect(() => {
    if (initialValues) {
      form.resetFields();
    }
  }, [initialValues]);

  return (
    <Fragment>
      <div className={bodyClassName}>
        {
          !initialValues ? (
            <Row>
              <Col xs={24} className="d-flex justify-content-center">
                <Spin spinning />
              </Col>
            </Row>
          ) : (
            <Form
              layout="vertical"
              form={form}
              name="StaticVariableForm"
              wrapperCol={24}
              labelCol={24}
              labelAlign="left"
              requiredMark={false}
              onValuesChange={() => {
                setHasPendingChanges();
              }}
              onFinish={handleSubmit}
              initialValues={initialValues}
              {...restProps}
            >
              <Row className="mb-2">
                <Col xs={24}>
                  <Form.Item
                    name="name"
                    label={
                      <Row>
                        <Col span={24}>
                          <Typography.Text>
                            {t('Name')}
                          </Typography.Text>
                        </Col>
                        <Col span={24}>
                          <Typography.Text type="secondary" className="fs-sm">
                            {t('Type variable name.')}
                          </Typography.Text>
                        </Col>
                      </Row>
                    }
                    rules={[
                      {
                        pattern: /^[a-zA-Z0-9_$\\.]*$/,
                        message: t('Variable names can only use letters, numbers, underscores, dollar signs, and dots.')
                      },
                      {
                        validator: (_, value) => {
                          value = value?.trim();
                          if (!value) {
                            return Promise.reject(new Error(t('Name cannot be blank.')));
                          }

                          if (!staticVariables || !staticVariables.items) {
                            return Promise.resolve();
                          }

                          if (initialValues && value === initialValues.name) {
                            return Promise.resolve();
                          }

                          const nameExists = staticVariables.items.some(variable =>
                            variable.name && variable.name === value
                          );

                          if (nameExists) {
                            return Promise.reject(new Error(t('A variable with this name already exists.')));
                          }

                          return Promise.resolve();
                        },
                        validateTrigger: ['onChange', 'onBlur']
                      }]
                    }
                    validateTrigger={['onChange', 'onBlur']}
                    getValueFromEvent={e => e.target.value?.trim()}
                  >
                    <Input
                      size="large"
                      disabled={!!initialValues?.name}
                      placeholder={t('Enter a name for the variable')}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row className="mb-2">
                <Col xs={24}>
                  <Form.Item
                    name="value"
                    rules={[
                      {
                        required: true,
                        validator: (_, value) => {
                          if (!value || value?.trim() === '') {
                            return Promise.reject(new Error(t('Value cannot be blank.')));
                          }
                          return Promise.resolve();
                        }
                      }
                    ]}
                    label={
                      <Row>
                        <Col span={24}>
                          <Typography.Text>
                            {t('Value')}
                          </Typography.Text>
                        </Col>
                        <Col span={24}>
                          <Typography.Text type="secondary" className="fs-sm">
                            {t('Enter a value for the variable.')}
                          </Typography.Text>
                        </Col>
                      </Row>
                    }
                  >
                    <Input.TextArea
                      size="large"
                      placeholder="Enter a value for the variable"
                      autoSize={true}
                      className="resizable-textarea"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row className="mb-2">
                <Col xs={24}>
                  <Form.Item
                    name="description"
                    label={
                      <Row>
                        <Col span={24}>
                          <Typography.Text>
                            {t('Description')}
                          </Typography.Text>
                        </Col>
                        <Col span={24}>
                          <Typography.Text type="secondary" className="fs-sm">
                            {t('Describe the variable.')}
                          </Typography.Text>
                        </Col>
                      </Row>
                    }
                  >
                    <Input.TextArea
                      size="large"
                      placeholder={t('Enter a description for the variable')}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row className="mb-2">
                <Col xs={24}>
                  <Form.Item
                    name="isConstant"
                    valuePropName="checked"
                    label={
                      <Row>
                        <Col span={24}>
                          <Typography.Text>
                            {t('Constant')}
                          </Typography.Text>
                        </Col>
                        <Col span={24}>
                          <Typography.Text type="secondary" className="fs-sm">
                            {t('If enabled, the variable will be treated as a constant and its value cannot be changed at runtime.')}
                          </Typography.Text>
                        </Col>
                      </Row>
                    }
                  >
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          )
        }
      </div>

      <div className={footerClassName}>
        <Row justify="end" gutter={8}>
          <Col>
            <Button
              size="large"
              loading={isLoading}
              onClick={onCancel}
            >
              {t('Cancel')}
            </Button>
          </Col>

          <Col>
            <Button
              type="primary"
              loading={isLoading}
              onClick={() => form.submit()}
              size="large"
            >
              {t('Submit')}
            </Button>
          </Col>
        </Row>
      </div>
    </Fragment>
  );
};

StaticVariableForm.propTypes = {
  onSubmitSuccess: PropTypes.func,
  onCancel: PropTypes.func,
  bodyClassName: PropTypes.string,
  footerClassName: PropTypes.string,
  setHasPendingChanges: PropTypes.func,
  form: PropTypes.any,
  isLoading: PropTypes.bool
};

export default StaticVariableForm;
